const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD;
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL;

const API_URL_V2 =
  PUBLIC_IS_PROD === "TRUE"
    ? "https://app.humblytics.com/api/v2/"
    : PUBLIC_IS_LOCAL === "TRUE"
    ? "http://localhost:3000/api/v2/"
    : "https://humblytics.onrender.com/api/v2/";

export interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  fullKey?: string;
  createdAt: string;
  lastUsedAt: string | null;
  isActive: boolean;
  scope: "property" | "account";
}

interface CreateApiKeyResponse {
  apiKey: ApiKey;
  fullKey: string;
}

function normalizeApiKey(raw: any): ApiKey {
  return {
    id: raw.api_key_id ?? raw.id ?? "",
    name: raw.name ?? "",
    keyPrefix: raw.key_prefix ?? raw.keyPrefix ?? "",
    createdAt: raw.created_at ?? raw.createdAt ?? "",
    lastUsedAt: raw.last_used_at ?? raw.lastUsedAt ?? null,
    isActive: raw.is_active ?? raw.isActive ?? true,
    scope: raw.scope === "account" ? "account" : "property",
  };
}

export class ApiAccessForbiddenError extends Error {
  constructor() {
    super("API access requires a Scale plan or higher");
    this.name = "ApiAccessForbiddenError";
  }
}

// ── Property-scoped keys ──────────────────────────────────────────────────────

export const getApiKeys = async (
  propertyId: string,
  token: string
): Promise<ApiKey[]> => {
  const response = await fetch(`${API_URL_V2}property/${propertyId}/api-keys`, {
    method: "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  if (response.status === 403) throw new ApiAccessForbiddenError();
  if (!response.ok) throw new Error(`Failed to fetch API keys: ${response.status}`);
  const data = await response.json();
  const raw: any[] = data.apiKeys ?? data ?? [];
  return raw.map(normalizeApiKey);
};

export const createApiKey = async (
  propertyId: string,
  name: string,
  token: string
): Promise<CreateApiKeyResponse> => {
  const response = await fetch(`${API_URL_V2}property/${propertyId}/api-keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
    body: JSON.stringify({ name, permissions: {} }),
  });
  if (!response.ok) throw new Error(`Failed to create API key: ${response.status}`);
  const data = await response.json();
  return {
    fullKey: data.apiKey ?? "",
    apiKey: normalizeApiKey({
      api_key_id: data.apiKeyId,
      name: data.name,
      key_prefix: data.apiKey ? data.apiKey.slice(0, 12) + "..." : "",
      created_at: data.createdAt,
      last_used_at: null,
      is_active: true,
      scope: "property",
    }),
  };
};

export const revokeApiKey = async (
  propertyId: string,
  keyId: string,
  token: string
): Promise<void> => {
  const response = await fetch(`${API_URL_V2}property/${propertyId}/api-keys/${keyId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  if (!response.ok) throw new Error(`Failed to revoke API key: ${response.status}`);
};

// ── Account-scoped keys ───────────────────────────────────────────────────────

export const getAccountApiKeys = async (token: string): Promise<ApiKey[]> => {
  const response = await fetch(`${API_URL_V2}account/api-keys`, {
    method: "GET",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  if (response.status === 403) throw new ApiAccessForbiddenError();
  if (!response.ok) throw new Error(`Failed to fetch account API keys: ${response.status}`);
  const data = await response.json();
  const raw: any[] = data.apiKeys ?? data ?? [];
  return raw.map((k: any) => normalizeApiKey({ ...k, scope: "account" }));
};

export const createAccountApiKey = async (
  name: string,
  token: string
): Promise<CreateApiKeyResponse> => {
  const response = await fetch(`${API_URL_V2}account/api-keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
    body: JSON.stringify({ name }),
  });
  if (response.status === 403) throw new ApiAccessForbiddenError();
  if (!response.ok) throw new Error(`Failed to create account API key: ${response.status}`);
  const data = await response.json();
  return {
    fullKey: data.apiKey ?? "",
    apiKey: normalizeApiKey({
      api_key_id: data.apiKeyId,
      name: data.name,
      key_prefix: data.apiKey ? data.apiKey.slice(0, 12) + "..." : "",
      created_at: data.createdAt,
      last_used_at: null,
      is_active: true,
      scope: "account",
    }),
  };
};

export const revokeAccountApiKey = async (
  keyId: string,
  token: string
): Promise<void> => {
  const response = await fetch(`${API_URL_V2}account/api-keys/${keyId}`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
  });
  if (!response.ok) throw new Error(`Failed to revoke account API key: ${response.status}`);
};
